import { Button } from '@creditornot/cb-button';
import { DateRangePicker } from '@creditornot/cb-date-picker';
import { cbSemanticCssVars, colors, semanticColors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';

import { DateRange } from '../../types';
import { trackCampaignViewInteractionClickDatePicker } from '../../utils/telemetry/telemetry';

export type Period = 'yesterday' | 'this-week' | 'last-week' | 'last-28-days';

export type FilterDateRangePickerProps = {
  value?: DateRange;
  onDateRangeChange: (period: DateRange) => void;
  timezone: string;
};

const MAXIMUM_TIMESPAN = { months: 6 };

const PRESET_TIME_RANGES = [
  { period: 'yesterday', messageId: 'purchases.labels.yesterday' },
  { period: 'this-week', messageId: 'purchases.labels.this-week' },
  { period: 'last-week', messageId: 'purchases.labels.last-week' },
  { period: 'last-28-days', messageId: 'insights.date-picker.last-28' },
] as const;

export const transformPeriodToDateRange = (period: Period, timezone: string): DateRange => {
  const today = DateTime.now().setZone(timezone).startOf('day');
  switch (period) {
    case 'yesterday':
      return { from: today.minus({ days: 1 }), to: today.minus({ days: 1 }) };
    case 'this-week':
      return { from: today.startOf('week'), to: today.endOf('week') };
    case 'last-week':
      return {
        from: today.minus({ weeks: 1 }).startOf('week'),
        to: today.minus({ weeks: 1 }).endOf('week'),
      };
    case 'last-28-days':
      return { from: today.minus({ days: 27 }), to: today };
  }
};

const FilterDateRangePickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 270px;
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;

  /*** scrollbar customization ***/

  /* Firefox */
  scrollbar-width: thin;
  /* ignore-color */
  scrollbar-color: ${colors.pepper32};

  /* Chrome/Edge/Safari */
  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.pepper100 })};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${cssThemedValue({ light: colors.pepper32, dark: colors.salt36 })};
    border-radius: 20px;
    border: 3px solid ${({ theme }) => semanticColors[theme.cbThemeMode].surfaceMain};
  }
`;

const RowDivider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
`;

const DataDelayNotice = styled.span`
  ${typographyCss.Small};
  color: ${cbSemanticCssVars.textSecondary};
  text-align: center;
`;

const PresetButtonsContainer = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const DateRangePickerWrapper = styled.div`
  padding: 0 16px 16px;
`;

export const FilterDateRangePicker = ({ value, onDateRangeChange, timezone }: FilterDateRangePickerProps) => {
  const [now] = useState(Date.now());

  const dateRange = value;

  const disabledDays = useMemo(
    () => ({
      before: DateTime.fromMillis(now).minus(MAXIMUM_TIMESPAN).toJSDate(),
      after: new Date(now),
    }),
    [now],
  );

  const updateDateRange = ({ from, to }: { from: Date | undefined; to?: Date | undefined }) => {
    if (!from) {
      return;
    }
    onDateRangeChange({
      from: DateTime.fromJSDate(from, { zone: timezone }).startOf('day'),
      to: DateTime.fromJSDate(to ?? from, { zone: timezone }).startOf('day'),
    });
  };

  const onPresetSelect = (period: Period) => {
    const dateRange = transformPeriodToDateRange(period, timezone);
    onDateRangeChange(dateRange);

    trackCampaignViewInteractionClickDatePicker(period);
  };

  const yesterdayDate = DateTime.now().setZone(timezone).minus({ days: 1 }).startOf('day');
  const shouldShowDataDelayNotice = dateRange?.to && dateRange.to >= yesterdayDate;

  return (
    <FilterDateRangePickerContainer>
      <DateRangePickerWrapper>
        <DateRangePicker
          disabledDays={disabledDays}
          value={{
            from: dateRange?.from.toJSDate(),
            to: dateRange?.to.toJSDate(),
          }}
          onChange={updateDateRange}
          timezone={timezone}
        />
      </DateRangePickerWrapper>
      <RowDivider />
      <PresetButtonsContainer>
        {PRESET_TIME_RANGES.map(({ period, messageId }) => (
          <Button stretch variant="lightBlue" size="small" onClick={() => onPresetSelect(period)} key={period}>
            <FormattedMessage id={messageId} />
          </Button>
        ))}
        {shouldShowDataDelayNotice && (
          <DataDelayNotice>
            <FormattedMessage id="campaigns.analytics.data-delay-notice" />
          </DataDelayNotice>
        )}
      </PresetButtonsContainer>
    </FilterDateRangePickerContainer>
  );
};
